import styles from "./Notifications.module.scss";
import { NotificationData } from "../../models/notification.model";
import notificationsService from "../../services/notifications.service";
import { useContext, useEffect, useState } from "react";
import SpinnerArea from "../../components/SpinnerArea";
import { Box, Button, List, Paper } from "@mui/material";
import { t } from "i18next";
import { useNavigate } from "react-router-dom";
import NotificationItem from "./NotificationItem";
import MainContext from "../../context/mainContext";
import NoRowsMessage from "../../components/Table/NoRowsMessage";

interface Props {
  isOpen: boolean;
  handleOnChange: (close: boolean) => void;
}

const NotificationList: React.FC<Props> = ({ isOpen, handleOnChange }) => {
  const { service } = useContext(MainContext);
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<NotificationData[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      search();
    }
  }, [isOpen]);

  const search = () => {
    service(() => notificationsService.getAll(), {
      callback: (result) => {
        setNotifications(result);
      },
      loader: setLoading,
    });
  };

  const handleMarkAllRead = () => {
    service(() => notificationsService.markAllRead(), {
      callback: () => {
        const changed = notifications.map((o) => ({
          ...o,
          isReaded: true,
        }));
        setNotifications(changed);
        handleOnChange(false);
      },
      loader: setLoading,
    });
  };

  const handleDeleteAllRead = () => {
    service(() => notificationsService.deleteAllRead(), {
      callback: () => {
        const result = notifications.filter(function (item, idx) {
          return !item.isReaded;
        });
        setNotifications(result);
      },
      loader: setLoading,
    });
  };

  const handleOnClick = (item: NotificationData) => {
    service(() => notificationsService.markRead(item.id), {
      callback: (result) => {
        handleOnChange(true);
        navigate("/" + item.link.replace(process.env.REACT_APP_MAIN_URL!, ""), {
          relative: "path",
          replace: false,
        });
      },
      loader: setLoading,
    });
  };

  const handleOnRemove = (id: number) => {
    service(() => notificationsService.remove(id), {
      callback: () => {
        const result = notifications.filter(function (item, idx) {
          return item.id !== id;
        });
        handleOnChange(false);
        setNotifications(result);
      },
      loader: setLoading,
    });
  };

  return (
    <>
      <SpinnerArea loading={loading}>
        <Box className={styles.main}>
          <Paper elevation={3} className={styles.basic}>
            {notifications.length > 0 ? (
              <div className={styles.header}>
                <Button
                  className={styles.markAllAsReadLink}
                  variant="text"
                  onClick={() => handleMarkAllRead()}
                >
                  {t("markAllAsRead")}
                </Button>
                <Button
                  className={styles.deleteAllReadNotificationsLink}
                  variant="text"
                  onClick={() => handleDeleteAllRead()}
                >
                  {t("deleteAllReadNotifications")}
                </Button>
              </div>
            ) : (
              <></>
            )}
            <List className={styles.list}>
              {notifications.length > 0 ? (
                notifications.map((notification) => {
                  return (
                    <NotificationItem
                      key={`news_${notification.id}`}
                      item={notification}
                      handleOnClick={handleOnClick}
                      handleOnRemove={handleOnRemove}
                    />
                  );
                })
              ) : (
                <NoRowsMessage
                  emptyMessage={t("noRowsMessage", {
                    entity: t("notifications"),
                  })}
                />
              )}
            </List>
          </Paper>
        </Box>
      </SpinnerArea>
    </>
  );
};

export default NotificationList;
